import React from 'react';

import {Owner} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {getStore} from 'redux-stores/Store';
import * as AssaySchemaDAO from 'results/assay-schemas/AssaySchemaDAO';
import SchemaMetadataSection from 'results/assay-schemas/SchemaMetadataSection';
import {ConfigureSchemaSpec} from 'schemas/spec/ConfigureSchemaSpec';
import {SCHEMA_TYPE_NAMES, SCHEMA_TYPES, SchemaSpec, SchemaType} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import ErrorList from 'ui-library/spec-editor/ErrorList';

type EditableSchemaType = Exclude<SchemaType, 'BOX' | 'ENTITY' | 'PLATE' | 'LOCATION' | 'PROCESS'>;

export interface CreateSchemaProps {
  schemaType: EditableSchemaType;
  createSchema: Function;
  listingUrl: string;
  canCreateWarehouseTableName: boolean;
}
export interface ConnectedProps {
  errors: Array<string> | null;
  errorMessage: string | null;
  ownerChoices: Array<Owner> | null;
  ownerId: string;
  name: string;
  sqlIdentifier: string | null;
  specString: string;
  sessionId: string;
}
class CreateSchemaConnector extends DataConnector<CreateSchemaProps, ConnectedProps> {
  enhanceProps(): ConnectedProps {
    const schemaConfig = this.reduxState().ui.schemaConfig;
    const {errorState, ownerChoices, ownerId, name, sqlIdentifier, specString, sessionId} = schemaConfig;
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};
    return {
      errors,
      errorMessage,
      ownerChoices,
      // TODO(TypeScript): Determine if this ! is safe.
      ownerId: ownerId!,
      name: name == null ? '' : name,
      sqlIdentifier,
      specString: specString == null ? '' : specString,
      sessionId,
    };
  }
}

const defaultSchemaSpec: {
  [key in EditableSchemaType]: SchemaSpec;
} = {
  [SCHEMA_TYPES.ASSAY_REQUEST]: {fields: [], idPrefix: '', samplesSchema: [], taskSchemas: []},
  [SCHEMA_TYPES.ASSAY_RUN]: {fields: []},
  [SCHEMA_TYPES.ASSAY_RESULT]: {fields: []},
  [SCHEMA_TYPES.REQUEST_TASK]: {fields: []},
  [SCHEMA_TYPES.CONNECTION]: {fields: [], idPrefix: ''},
  [SCHEMA_TYPES.SCHEMA_INTERFACE]: {fields: []},
  [SCHEMA_TYPES.STUDY]: {fields: []},
};

export class CreateSchemaPage extends React.Component<CreateSchemaProps & ConnectedProps> {
  componentDidMount(): void {
    getStore().dispatch(SchemaConfigReduxActions.resetSchemaSpecUi());
    getStore().dispatch(
      SchemaConfigReduxActions.schemaSpecFetched(null, defaultSchemaSpec[this.props.schemaType])
    );
  }

  _handleCreate = (): void => {
    let spec;
    try {
      spec = JSON.parse(this.props.specString);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: 'Spec is not valid JSON',
        })
      );
      return;
    }
    this.props.createSchema({
      ownerId: this.props.ownerId,
      name: this.props.name,
      schemaType: this.props.schemaType,
      sqlIdentifier: this.props.sqlIdentifier || null,
      spec,
      sessionId: this.props.sessionId,
    });
  };

  _handleNavigateToSchemaListing = (): void => {
    BenchlingUtil.navigate(null, this.props.listingUrl);
  };

  render(): JSX.Element {
    if (this.props.ownerChoices == null) {
      return <HorizontalThrobber key="throbber" />;
    }
    if (this.props.ownerChoices.length === 0) {
      // Technically you can have owner choices and not be an admin - in that case we rely on a 400
      // when you try to create it.
      return <div>You must be an admin of an org to create schemas.</div>;
    }
    const schemaTypeName = SCHEMA_TYPE_NAMES[this.props.schemaType];
    return (
      <SettingsPageContainer
        onClickTitle={this._handleNavigateToSchemaListing}
        title={`${schemaTypeName} Schemas`}
        subtitle={`Create ${schemaTypeName} Schema`}
      >
        <div className="u-flex-grow">
          <SchemaMetadataSection
            isEditableWarehouseTableName={true}
            hideWarehouseTableName={!this.props.canCreateWarehouseTableName}
            name={this.props.name}
            sqlIdentifier={this.props.sqlIdentifier}
            ownerId={this.props.ownerId}
            ownerChoices={this.props.ownerChoices}
            onNameChange={(name: string) => {
              getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecName(name));
            }}
            onOwnerChange={(owner) => {
              getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecOwner(owner.id));
            }}
            onSqlIdentifierChange={(sqlIdentifier: string) => {
              getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecSqlIdentifier(sqlIdentifier));
            }}
          />
          <div>
            <ErrorList errors={this.props.errors} errorMessage={this.props.errorMessage} />
            <ConfigureSchemaSpec
              link={{
                value: this.props.specString,
                onChange: (e) => {
                  getStore().dispatch(SchemaConfigReduxActions.schemaSpecChanged(e.target.value));
                },
              }}
            />
          </div>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={!this.props.name || !this.props.specString}
            onClick={this._handleCreate}
            size={ButtonSize.MEDIUM}
            text="Create"
            dataTestAttrs={{component: 'CreateSchemaPage', key: ModalButtonKey.SUBMIT}}
          />
          {(this.props.schemaType === SCHEMA_TYPES.ASSAY_RUN ||
            this.props.schemaType === SCHEMA_TYPES.ASSAY_RESULT) && (
            <Button
              className="u-small-spacing-right"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MEDIUM}
              onClick={() => {
                BenchlingUtil.navigate(null, AssaySchemaDAO.createAssaySchemaURL(this.props.schemaType));
              }}
              text="Edit in UI"
              dataTestAttrs={{component: 'CreateSchemaPage', element: 'switchUI'}}
            />
          )}
        </div>
      </SettingsPageContainer>
    );
  }
}

export default CreateSchemaConnector.connect(CreateSchemaPage);
